import {
  changedEditValues,
  createEditValuesSchema,
  initialEditValues,
  resolveEditOptionsForUser,
  withPayItemEditOptions,
  type EditOptions,
  type EditValues,
} from '@cims-online/shared'
import { useAuthContext } from 'context/AuthContext'
import { useProjectContext } from 'context/ProjectContext'
import { errorHandler } from 'hooks/errorHandler'
import { simpleEditNotification } from 'hooks/notificationHandler'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  useEditWorkflowRecordFromRecordsMutation,
  useWorkflowRecordEditOptionsLazyQuery,
  useWorkflowRecordEditPayItemsQuery,
} from 'schema'
import {
  DefaultMutationHookOptions,
  DefaultQueryHookOptions,
} from 'utils/defaults'
import type { EditWorkflowRecordDrawerProps } from '../types'

/**
 * Loads fresh actor-neutral options, projects them for the current user,
 * supplies project pay-item options, including deleted pay items for fields
 * that allow them, and initializes transformed field values.
 * It submits only valid values that differ from their database baselines,
 * prevents duplicate requests, retains drafts after failed saves, and
 * distinguishes load retries from save retries.
 */
export function useEditWorkflowRecord({
  recordId,
  onClose,
  onEdited,
}: EditWorkflowRecordDrawerProps) {
  const { user } = useAuthContext()
  const { projectId } = useProjectContext()
  const [loaded, setLoaded] = useState<EditOptions | null>(null)
  const [values, setValues] = useState<EditValues>({})
  const [saveFailure, setSaveFailure] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [loadAttempt, setLoadAttempt] = useState(0)
  const busy = useRef(false)
  // A new identity starts a fresh options load. Comparing it with the settled
  // request derives the loading state instead of resetting state in the effect.
  const request = useMemo(
    () => ({ recordId, projectId, user, loadAttempt }),
    [recordId, projectId, user, loadAttempt]
  )
  const [settled, setSettled] = useState<{
    request: typeof request
    failure: string | null
  } | null>(null)
  const loadingOptions = settled?.request !== request
  const [load] = useWorkflowRecordEditOptionsLazyQuery({
    ...DefaultQueryHookOptions,
    onError: undefined,
  })
  const [mutate] = useEditWorkflowRecordFromRecordsMutation({
    ...DefaultMutationHookOptions,
    onError: undefined,
  })

  useEffect(() => {
    let active = true
    const { recordId, projectId, user } = request
    load({ variables: { id: recordId } })
      .then((result) => {
        if (!active) return
        const fresh = result.data?.listWorkflowRecords.records[0]?.editOptions
        if (!fresh) throw new Error('The record is unavailable for editing')
        if (!user) throw new Error('Sign in to edit this record')
        const resolved = resolveEditOptionsForUser(
          fresh,
          user.id,
          user.admin(projectId).authorized
        )
        setLoaded(resolved)
        setValues(initialEditValues(resolved.fields))
        setSaveFailure(null)
        setSettled({ request, failure: null })
      })
      .catch((error: unknown) => {
        if (!active) return
        setSaveFailure(null)
        setSettled({
          request,
          failure:
            error instanceof Error
              ? error.message
              : 'Unable to load edit options',
        })
        errorHandler(error)
      })
    return () => {
      active = false
    }
  }, [load, request])
  // A pending load hides earlier load and save failures.
  const failure = loadingOptions
    ? null
    : saveFailure ?? settled?.failure ?? null

  // Pay-item options and labels come from the project list, not the response.
  // The shared PayItemSelect query excludes deleted pay items, so it cannot
  // keep saved selections valid on fields that allow them.
  const hasPayItems = !!loaded?.fields.some(({ type }) => type === 'pay_items')
  const { data: payItemData, loading: payItemsLoading } =
    useWorkflowRecordEditPayItemsQuery({
      ...DefaultQueryHookOptions,
      variables: { projectId },
      skip: !hasPayItems,
    })
  const payItems = payItemData?.payItemNoGeometries.records
  const options = useMemo(
    () =>
      loaded &&
      withPayItemEditOptions(
        loaded,
        (payItems ?? []).map(({ globalId, label, deleted }) => ({
          id: globalId,
          label,
          tooltip: null,
          active: !deleted,
        }))
      ),
    [loaded, payItems]
  )
  const loading = loadingOptions || (hasPayItems && payItemsLoading)

  const changes = options ? changedEditValues(options.fields, values) : {}
  const validation = options
    ? createEditValuesSchema(options.fields).safeParse(changes)
    : null
  const canSubmit =
    !!options?.enabled &&
    !!validation?.success &&
    Object.keys(changes).length > 0 &&
    !saving &&
    !loading

  async function submit() {
    if (busy.current || !canSubmit || !validation?.success) return
    busy.current = true
    setSaving(true)
    setSaveFailure(null)
    try {
      const result = await mutate({
        variables: { input: { id: recordId, values: validation.data } },
      })
      const edited = result.data?.editWorkflowRecord.edited
      if (!edited) throw new Error('No save confirmation was received.')
      onClose()
      try {
        await onEdited()
      } catch (error) {
        errorHandler(error)
      } finally {
        simpleEditNotification(edited.recordName)
      }
    } catch (error) {
      setSaveFailure(
        error instanceof Error ? error.message : 'Unable to save the record'
      )
      errorHandler(error)
    } finally {
      busy.current = false
      setSaving(false)
    }
  }

  return {
    options,
    values,
    failure,
    loading,
    saving,
    submit,
    retry: async () => {
      if (busy.current || loading) return
      if (options) await submit()
      else setLoadAttempt((current) => current + 1)
    },
    frozen: saving || loading || !options?.enabled,
    canSubmit,
    errors: validation && !validation.success ? validation.error.issues : [],
    change: (id: string, value: EditValues[string]) => {
      if (!busy.current) setValues((current) => ({ ...current, [id]: value }))
    },
    close: () => {
      if (!busy.current) onClose()
    },
  }
}
